import { useEffect, useState } from 'react';
import { ABAC } from '../../../primary-record-types';
import { getIntl } from '../../../utils/intl';
import { usePromptButton } from '../PromptButton';
import Text from '../../v2BaseComponents/Text';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import { ContentStack } from '../../v2BaseComponents/Stack';
import { PromptActions } from '../../v2BaseComponents/Prompt';

const intl = getIntl('useCollaboratorEditWarningPrompt', {
    promptTitle: 'Warning',
    promptMessage:
        'Adding the edit permission for collaborators will allow them to invite or remove other collaborators and give others access to view this profile.',
    promptAccept: 'Accept',
    promptCancel: 'Cancel',
});

export type UseCollaboratorEditWarningPromptArgs = {
    appPermissions: ABAC.AppPermissions;
    onChange: (value: ABAC.AppPermissions) => void;
    initialAppPermissions: ABAC.AppPermissions;
};

export type UseCollaboratorEditWarningPromptReturnValue = {
    Prompt: React.ReactNode;
};

export const useCollaboratorEditWarningPrompt = ({
    appPermissions,
    onChange,
    initialAppPermissions,
}: UseCollaboratorEditWarningPromptArgs) => {
    const [disablePrompt, setDisablePrompt] = useState(initialAppPermissions.collaborators === 'edit');

    const promptButton = usePromptButton({
        renderPromptContents: (onClose) => (
            <MainContentContainer>
                <ContentStack>
                    <Text variant="body-1">{intl('promptMessage')}</Text>
                    <PromptActions
                        actions={[
                            {
                                title: intl('promptAccept'),
                                onPress: () => {
                                    setDisablePrompt(true);
                                    onClose();
                                },
                                variant: 'primary',
                                icon: 'CheckCircle',
                                testID: 'accept-collaborator-edit-warning',
                            },
                            {
                                title: intl('promptCancel'),
                                onPress: () => {
                                    onChange({ ...appPermissions, collaborators: 'view' });
                                    setDisablePrompt(false);
                                    onClose();
                                },
                                variant: 'secondary',
                                icon: 'XCircle',
                                testID: 'reject-collaborator-edit-warning',
                            },
                        ]}
                    />
                </ContentStack>
            </MainContentContainer>
        ),
        promptTitle: intl('promptTitle'),
    });

    useEffect(() => {
        if (
            appPermissions.collaborators === 'edit' &&
            !disablePrompt &&
            initialAppPermissions?.collaborators !== 'edit'
        ) {
            promptButton.onPress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appPermissions, disablePrompt, initialAppPermissions?.collaborators]);

    return {
        Prompt: promptButton.Prompt,
    };
};
